/**
 * ChatAnswer — render-only formatter for assistant chat bubbles.
 *
 * Why this exists:
 *   The model is instructed (in CHAT_SYSTEM_PROMPT — which we must NOT change)
 *   to use light markdown (** bold **, "- " bullets) and inline citations like
 *   "(R1, ~0:36)". Rendering m.content raw made users see literal asterisks and
 *   cluttered mid-sentence citations.
 *
 * What this does (purely at RENDER time — the stored message content is never
 * mutated, so the audit/PDF export keeps full inline citations):
 *   1. Renders **bold** and bullet lists; strips any stray markdown asterisks
 *      so no literal "*" / "**" ever reaches the screen.
 *   2. Pulls citations out of the prose and renders them as small, muted
 *      "R1 · 0:36" source chips at the END of the relevant point — present but
 *      unobtrusive, never inline mid-sentence.
 */
import React from "react";

// A parenthetical that contains an MM:SS timestamp, e.g.
//   (R1, ~0:36)  ·  (Round 2, ~12:30)  ·  (R1, ~0:36; R2, ~5:00)  ·  (~22:00)
const PAREN_WITH_TIME = /\(([^()]*\d{1,2}:\d{2}[^()]*)\)/g;
const ROUND_TIME_PAIR = /R(?:ound)?\.?\s*(\d+)\s*[,;]?\s*~?\s*(\d{1,2}:\d{2})/gi;
const TIME_ONLY = /~?\s*(\d{1,2}:\d{2})/g;

export function extractCitations(text) {
  const citations = [];
  const cleaned = String(text || "").replace(
    PAREN_WITH_TIME,
    (full, inner, offset, whole) => {
      ROUND_TIME_PAIR.lastIndex = 0;
      let m;
      let matched = false;
      while ((m = ROUND_TIME_PAIR.exec(inner)) !== null) {
        citations.push({ round: m[1], time: m[2] });
        matched = true;
      }
      if (!matched) {
        // Time only (e.g. "Round 1 (~22:00)") — look back for a preceding round.
        const before = whole.slice(Math.max(0, offset - 24), offset);
        const rm = before.match(/R(?:ound)?\.?\s*(\d+)\s*$/i);
        const round = rm ? rm[1] : null;
        TIME_ONLY.lastIndex = 0;
        let tm;
        while ((tm = TIME_ONLY.exec(inner)) !== null) {
          citations.push({ round, time: tm[1] });
          matched = true;
        }
      }
      return matched ? "" : full;
    },
  );
  const tidy = cleaned
    .replace(/\(\s*[;,]?\s*\)/g, "") // leftover empty parens
    .replace(/\s+([.,;:!?])/g, "$1") // space before punctuation
    .replace(/[ \t]{2,}/g, " ")
    .trim();
  return { text: tidy, citations };
}

// Render **bold** / __bold__ inline and strip any stray markdown asterisks so
// no literal "*" is ever shown. Underscores in text (e.g. snake_case) are left.
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <strong key={`${keyPrefix}-b${i}`}>{p.slice(2, -2)}</strong>;
    }
    if (/^__[^_]+__$/.test(p)) {
      return <strong key={`${keyPrefix}-u${i}`}>{p.slice(2, -2)}</strong>;
    }
    const clean = p.replace(/\*\*/g, "").replace(/\*/g, "");
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{clean}</React.Fragment>;
  });
}

function CitationChips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <span
      className="ml-1.5 inline-flex flex-wrap gap-1 align-middle"
      data-testid="chat-citations"
    >
      {items.map((c, i) => (
        <span
          key={i}
          data-testid="chat-citation-chip"
          className="inline-flex items-center rounded-full bg-ink-100 px-1.5 py-[1px] text-[10px] font-medium leading-tight text-ink-500"
          title={`Source: ${c.round ? `Round ${c.round}, ` : ""}${c.time}`}
        >
          {c.round ? `R${c.round} · ` : ""}
          {c.time}
        </span>
      ))}
    </span>
  );
}

export default function ChatAnswer({ content }) {
  const lines = String(content || "").split("\n");
  const blocks = [];
  let bullets = null;
  const flushBullets = () => {
    if (bullets && bullets.length) blocks.push({ type: "ul", items: bullets });
    bullets = null;
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      return;
    }
    const bm = line.match(/^([-*•])\s+(.*)$/);
    if (bm) {
      if (!bullets) bullets = [];
      bullets.push(bm[2]);
    } else {
      flushBullets();
      blocks.push({ type: "p", text: line });
    }
  });
  flushBullets();

  return (
    <div className="space-y-1.5" data-testid="chat-answer">
      {blocks.map((b, i) => {
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc space-y-1 pl-4 marker:text-ink-300">
              {b.items.map((it, j) => {
                const { text, citations } = extractCitations(it);
                return (
                  <li key={j}>
                    {renderInline(text, `${i}-${j}`)}
                    <CitationChips items={citations} />
                  </li>
                );
              })}
            </ul>
          );
        }
        const { text, citations } = extractCitations(b.text);
        return (
          <p key={i}>
            {renderInline(text, `p${i}`)}
            <CitationChips items={citations} />
          </p>
        );
      })}
    </div>
  );
}
